import { useState, useMemo, useEffect, Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { useAlertsStore } from '../store/alerts';
import { Panel } from '../components/ui/Panel';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { StatusBadge } from '../components/ui/StatusBadge';
import { SectionHeader } from '../components/ui/SectionHeader';
import { RangePicker, type Range } from '../components/ui/RangePicker';
import { Target, Search, RotateCcw, DollarSign, Download, Smartphone, Monitor, Tablet, Bot, HelpCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { formatCurrency, formatRelativeTime, formatDateTime, getDeviceLabel } from '../lib/utils';
import { CountryFlag } from '../components/ui/CountryFlag';
import { SubIdLightning } from '../components/SubIdLightning';
import { ConversionEventsPanel } from '../components/subdetail/ConversionEventsPanel';
import { useAuth } from '../store/auth';
import { usePollMultiplier } from '../hooks/usePollMultiplier';
import { useOfferLookup } from '../hooks/useLookups';

// Match the rest of the dashboard's range vocabulary — "yesterday" is a
// common publisher need (verify daily payout numbers, compare to today),
// "90d" rarely used so removed in favor of explicit yesterday slot.
// Range type comes from ui/RangePicker (shared with other pages).

function utcDateInput(ts = Date.now()): string {
  return new Date(ts).toISOString().slice(0, 10);
}

interface Conversion {
  id: number;
  clickId: string;
  subId: string | null;
  offerId: number | null;
  country: string | null;
  deviceType: string | null;
  source: string | null;
  revenue: number;
  payout: number;
  status: string;
  eventType: string;
  networkOfferName: string | null;
  networkGoal: string | null;
  payoutModel: string | null;
  createdAt: number;
  approvedAt: number | null;
  postbackReceivedAt: number | null;
}

function DeviceIcon({ device, className = '' }: { device: string | null | undefined; className?: string }) {
  const d = (device ?? '').toLowerCase();
  const iconClass = `inline-block ${className}`;
  if (d.includes('bot')) return <Bot size={12} className={`${iconClass} text-neon-orange`} />;
  if (d.includes('mobile')) return <Smartphone size={12} className={`${iconClass} text-neon-cyan`} />;
  if (d.includes('tablet')) return <Tablet size={12} className={`${iconClass} text-neon-purple`} />;
  if (d.includes('desktop')) return <Monitor size={12} className={`${iconClass} text-neon-green`} />;
  return <HelpCircle size={12} className={`${iconClass} text-text-tertiary`} />;
}

/**
 * Color-coded badge for the network's payout model.
 *
 * Heuristics live in postback worker (derivePayoutModel). Here we just style.
 * If `payoutModel` is null, fall back to eventType (e.g. "sale", "lead") so old
 * conversions before this column existed still show something sensible.
 */
function PayoutModelBadge({ model, fallback }: { model: string | null; fallback?: string }) {
  const value = (model || fallback || '').toLowerCase();
  if (!value || value === 'other') {
    return (
      <span className="text-[10px] font-mono uppercase tracking-wider text-text-tertiary bg-bg-tertiary px-2 py-0.5 rounded border border-border-subtle">
        {fallback || '—'}
      </span>
    );
  }
  // Color map — chosen so each model has a distinct neon hue
  const styles: Record<string, string> = {
    cpa: 'border-neon-cyan/40 text-neon-cyan bg-neon-cyan/5',
    sale: 'border-neon-cyan/40 text-neon-cyan bg-neon-cyan/5',
    cps: 'border-neon-cyan/40 text-neon-cyan bg-neon-cyan/5',
    cpl: 'border-neon-yellow/40 text-neon-yellow bg-neon-yellow/5',
    lead: 'border-neon-yellow/40 text-neon-yellow bg-neon-yellow/5',
    soi: 'border-neon-green/40 text-neon-green bg-neon-green/5',
    doi: 'border-neon-magenta/40 text-neon-magenta bg-neon-magenta/5',
    install: 'border-neon-purple/40 text-neon-purple bg-neon-purple/5',
    cpi: 'border-neon-purple/40 text-neon-purple bg-neon-purple/5',
    revshare: 'border-neon-orange/40 text-neon-orange bg-neon-orange/5',
    trial: 'border-neon-yellow/40 text-neon-yellow bg-neon-yellow/5',
    cc_submit: 'border-neon-red/40 text-neon-red bg-neon-red/5',
  };
  const cls = styles[value] ?? 'border-border-subtle text-text-secondary bg-bg-tertiary';
  const label = value === 'cc_submit' ? 'CC' : value.toUpperCase();
  return (
    <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border ${cls}`}>
      {label}
    </span>
  );
}

export function ConversionsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const offerLookup = useOfferLookup();
  const pollMult = usePollMultiplier();
  // NOTE: reverted user-role auto-scoping per user request. Conversions
  // remains a network-wide view for both roles.
  const [range, setRange] = useState<Range>('today');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'approved' | 'pending' | 'rejected' | 'reversed'>('all');
  // P2-G multi-event: which conversion's child events are expanded (null = none)
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // While on /conversions, the global ConversionAlertProvider skips its
  // own polling (smart-skip — no point fetching same data twice). To avoid
  // a thundering-herd of toasts when user navigates AWAY from this page,
  // we update the alert provider's high-water mark (lastSeenConversionId)
  // as new entries appear here. The alert provider resumes polling from
  // that mark, so it only toasts entries that arrive AFTER user leaves.
  const setLastSeenId = useAlertsStore((s) => s.setLastSeenConversionId);
  const [customFrom, setCustomFrom] = useState(() => utcDateInput());
  const [customTo, setCustomTo] = useState(() => utcDateInput());
  const customValid = customFrom <= customTo;
  const rangeParams =
    range === 'custom'
      ? { range, from: customFrom, to: customTo }
      : { range };
  const rangeKey =
    range === 'custom' ? `${range}:${customFrom}:${customTo}` : range;

  // Limit scaled to range. Today: 50 rows is plenty for live monitoring
  // (search filter exists for drilling down). Longer ranges keep bigger
  // default for historical review. Cuts payload ~10x for the most
  // frequently-polled view.
  const conversionsLimit = range === 'today' ? 50 : 500;
  const list = useQuery({
    queryKey: ['conversions', rangeKey],
    queryFn: () =>
      apiClient.get<Conversion[]>('/api/v1/conversions', {
        // Use range param (server resolves via resolveRange) so the table
        // matches the KPI overview window for the same range — especially
        // important for "yesterday" which needs an upper-bound, not just
        // since_days backward-looking.
        ...rangeParams,
        limit: conversionsLimit,
        ...(statusFilter !== 'all' && { status: statusFilter }),
      }),
    refetchInterval: range === 'today' ? 10_000 * pollMult : false,
    enabled: range !== 'custom' || customValid,
  });

  // BUGFIX: previously KPI counts/sums were computed from `list.data` — but
  // list is capped at 50 rows for today (or 500 for longer ranges). So the
  // header showed "50 conversions, $160" when the DB actually had 157 / $450.
  // User caught this comparing with Statistics page: "Statistics dan
  // conversions kenapa berbeda?". Fix: pull totals from the same stats
  // overview endpoint Statistics uses. Tiny payload, server-side cached
  // (60s KV), no row-data dependency.
  const overview = useQuery({
    queryKey: ['conversions-kpi-overview', rangeKey],
    queryFn: () =>
      apiClient.get<{
        conversionsApproved: number;
        revenueGross: number;
        payoutGross: number;
      }>('/api/v1/stats/overview', rangeParams),
    refetchInterval: range === 'today' ? 10_000 * pollMult : false,
    enabled: range !== 'custom' || customValid,
  });

  // Sync max conversion id seen → alert provider's high-water mark, so when
  // user navigates away the global alert poller resumes from this point
  // (no duplicate toasts for entries already visible in this table).
  useEffect(() => {
    const rows = list.data;
    if (!rows || rows.length === 0) return;
    const maxId = Math.max(...rows.map((r) => r.id));
    if (maxId > 0) setLastSeenId(maxId);
  }, [list.data, setLastSeenId]);

  const filtered = useMemo(() => {
    let rows = list.data ?? [];
    // Legacy behavior: when statusFilter === 'all', hide reversed/rejected from stream
    // When specific status selected, show only that status (including reversed/rejected)
    if (statusFilter === 'all') {
      rows = rows.filter((r) => r.status !== 'reversed' && r.status !== 'rejected');
    }
    // else: status already filtered by API endpoint, don't exclude additional statuses
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((r) => {
        const offerName = offerLookup.getName(r.offerId);
        return (
          (r.subId ?? '').toLowerCase().includes(q) ||
          (r.clickId ?? '').toLowerCase().includes(q) ||
          (r.country ?? '').toLowerCase().includes(q) ||
          offerName.toLowerCase().includes(q) ||
          (r.networkOfferName ?? '').toLowerCase().includes(q) ||
          (r.networkGoal ?? '').toLowerCase().includes(q) ||
          (r.payoutModel ?? '').toLowerCase().includes(q)
        );
      });
    }
    return rows;
  }, [list.data, search, offerLookup, statusFilter]);

  const visibleTotals = useMemo(() => ({
    revenue: filtered.reduce((sum, row) => sum + (row.revenue ?? 0), 0),
  }), [filtered]);

  // Quick stats. Counts + sums come from the overview endpoint (full
  // aggregate, not capped at the table's 50-row fetch). Reversed counts
  // are derived from the visible rows because there's no dedicated
  // reversed aggregate yet — minor edge case (reversed conversions are
  // rare, usually 0). If reversed becomes operationally important, add
  // a `reversedCount` field server-side in overview.
  const stats = useMemo(() => {
    const ov = overview.data;
    const rows = list.data ?? [];
    const reversed = rows.filter((r) => r.status === 'reversed' || r.status === 'rejected');
    return {
      count: ov?.conversionsApproved ?? 0,
      revenue: ov?.revenueGross ?? 0,
      payout: ov?.payoutGross ?? 0,
      reversedCount: reversed.length,
      reversedRevenue: reversed.reduce((s, r) => s + (r.revenue ?? 0), 0),
    };
  }, [overview.data, list.data]);

  const handleExportCSV = () => {
    // Generate CSV from filtered data
    const headers = ['Time', 'Sub ID', 'Country', 'Device', 'Offer', 'Goal', 'Revenue', 'Status'];
    const rows = filtered.map(c => [
      formatDateTime(c.createdAt),
      c.subId || '',
      c.country || '',
      getDeviceLabel(c.deviceType),
      offerLookup.getName(c.offerId),
      c.payoutModel || c.eventType || '',
      c.revenue.toFixed(2),
      c.status,
    ]);
    
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `conversions-${rangeKey}.csv`;
    link.click();
  };

  return (
    <div className="w-full space-y-5 sm:space-y-6 animate-fade-in">
      {/* Header */}
      <SectionHeader
        title={isAdmin ? 'Conversions' : 'My Conversions'}
        subtitle={`postbacks dari iMonetizeIt / Trafee / network lain · ${filtered.length} records · ${
          range === 'custom' ? `${customFrom}..${customTo} UTC` : range
        }`}
        actions={
          <div className="flex flex-col sm:items-end gap-2">
            <RangePicker value={range} onChange={setRange} />
            {range === 'custom' && (
              <div className="flex flex-wrap items-center gap-2 text-xs font-mono">
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="input-cyber h-9 w-36"
                />
                <span className="text-text-tertiary">to</span>
                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="input-cyber h-9 w-36"
                />
                {!customValid && (
                  <span className="text-neon-red">from must be before to</span>
                )}
              </div>
            )}
          </div>
        }
      />

      {/* KPI Row - Three panels */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <Panel bracket level={1}>
          <div className="p-4">
            <div className="text-[10px] font-display uppercase tracking-wider text-neon-magenta mb-1.5">CONVERSIONS</div>
            <div className="text-3xl font-display font-bold text-neon-magenta numeric">{stats.count}</div>
          </div>
        </Panel>
        <Panel bracket level={1}>
          <div className="p-4">
            <div className="text-[10px] font-display uppercase tracking-wider text-neon-yellow mb-1.5 flex items-center gap-1">
              <DollarSign size={12} /> REVENUE
            </div>
            <div className="text-3xl font-display font-bold text-neon-yellow numeric">{formatCurrency(stats.revenue)}</div>
          </div>
        </Panel>
        <Panel bracket level={1}>
          <div className="p-4">
            <div className="text-[10px] font-display uppercase tracking-wider text-neon-red mb-1.5 flex items-center gap-1">
              <RotateCcw size={12} /> REVERSED
            </div>
            <div className="text-3xl font-display font-bold text-neon-red numeric">{stats.reversedCount}</div>
            {stats.reversedCount > 0 && (
              <div className="text-[10px] text-neon-red font-mono mt-1">−{formatCurrency(stats.reversedRevenue)}</div>
            )}
          </div>
        </Panel>
      </div>

      {/* Filter Strip */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="flex-1 max-w-md">
          <Input
            placeholder="Search Sub ID / Country / Offer / Goal..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            icon={<Search size={14} />}
          />
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="input-cyber h-9 px-3 min-w-[140px]"
          >
            <option value="all">All Status</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
            <option value="reversed">Reversed</option>
          </select>
          <Button variant="ghost" size="sm" icon={<Download size={14} />} onClick={handleExportCSV}>
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stream Table */}
      <Panel bracket level={1}>
        <div className="p-4 border-b border-[rgba(0,255,225,0.18)]">
          <h3 className="font-display text-sm uppercase tracking-wider text-neon-cyan flex items-center gap-2">
            <Target size={14} />
            Conversion Stream
            {list.isRefetching && (
              <span className="ml-2 text-[10px] text-text-tertiary font-mono">syncing...</span>
            )}
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[rgba(0,255,225,0.18)]">
                <th className="text-left text-[10px] font-display uppercase tracking-wider text-neon-cyan px-4 py-3">TIME</th>
                <th className="text-left text-[10px] font-display uppercase tracking-wider text-neon-cyan px-4 py-3">SUB ID</th>
                <th className="text-left text-[10px] font-display uppercase tracking-wider text-neon-cyan px-4 py-3">COUNTRY</th>
                <th className="text-left text-[10px] font-display uppercase tracking-wider text-neon-cyan px-4 py-3">DEVICE</th>
                <th className="text-left text-[10px] font-display uppercase tracking-wider text-neon-cyan px-4 py-3">OFFER</th>
                <th className="text-left text-[10px] font-display uppercase tracking-wider text-neon-cyan px-4 py-3">GOAL</th>
                <th className="text-right text-[10px] font-display uppercase tracking-wider text-neon-cyan px-4 py-3">REVENUE</th>
                <th className="text-left text-[10px] font-display uppercase tracking-wider text-neon-cyan px-4 py-3">STATUS</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <Fragment key={c.id}>
                  <tr
                    className={`cursor-pointer hover:bg-bg-secondary/60 ${c.status === 'reversed' || c.status === 'rejected' ? 'opacity-50' : ''}`}
                    onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}
                  >
                    <td className="px-4 py-3 font-mono text-xs text-text-secondary whitespace-nowrap" title={formatDateTime(c.createdAt)}>
                      <button
                        className="inline-flex mr-1.5 align-middle text-text-tertiary hover:text-neon-magenta"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedId(expandedId === c.id ? null : c.id);
                        }}
                        aria-label={expandedId === c.id ? 'Collapse events' : 'Expand events'}
                      >
                        {expandedId === c.id ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      </button>
                      {formatRelativeTime(c.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-neon-cyan">
                        {c.subId
                          ? <SubIdLightning subId={c.subId}>{c.subId}</SubIdLightning>
                          : <span className="text-text-tertiary">—</span>}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {c.country ? <CountryFlag iso2={c.country} showCode size={18} /> : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {c.deviceType ? (
                        <span className="flex items-center gap-1.5 font-mono text-xs">
                          <DeviceIcon device={c.deviceType} />
                          <span>{getDeviceLabel(c.deviceType)}</span>
                        </span>
                      ) : (
                        <span className="text-text-tertiary">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col leading-tight">
                        <span
                          className="font-mono text-xs text-neon-cyan truncate max-w-[150px] block"
                          title={`Offer ID: ${c.offerId ?? 'unlinked'}`}
                        >
                          {offerLookup.getName(c.offerId)}
                        </span>
                        {c.networkOfferName && (
                          <span
                            className="text-[10px] font-mono text-text-tertiary truncate max-w-[200px]"
                            title={c.networkOfferName}
                          >
                            {c.networkOfferName}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <PayoutModelBadge model={c.payoutModel} fallback={c.eventType} />
                    </td>
                    <td className="px-4 py-3 text-right numeric text-neon-yellow font-mono text-xs">
                      {formatCurrency(c.revenue)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={c.status} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ChevronRight size={12} className={`text-text-tertiary transition-transform ${expandedId === c.id ? 'rotate-90' : ''}`} />
                    </td>
                  </tr>
                  {expandedId === c.id && (
                    <tr>
                      <td colSpan={9}>
                        <ConversionEventsPanel conversionId={c.id} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
              {filtered.length === 0 && !list.isLoading && (
                <tr>
                  <td colSpan={9} className="text-center py-14 text-text-tertiary">
                    <Target className="mx-auto mb-3 opacity-30" size={36} />
                    <p className="font-mono text-xs">
                      {search ? 'no results matching your filter' : 'no conversions yet — waiting for postbacks from networks'}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr className="border-t border-[rgba(0,255,225,0.18)] bg-bg-tertiary/40">
                  <td colSpan={6} className="px-4 py-3 font-display text-xs uppercase tracking-wider text-text-secondary">
                    TOTAL
                    {search
                      ? ` · ${filtered.length} of ${list.data?.length ?? 0} (filtered)`
                      : ` · ${filtered.length} rows`}
                  </td>
                  <td className="px-4 py-3 text-right numeric font-display text-sm font-bold text-neon-yellow">
                    {formatCurrency(visibleTotals.revenue)}
                  </td>
                  <td />
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Panel>
    </div>
  );
}
